import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress, Typography } from '@mui/material';
import {
    GridCellModes,
    GridCellModesModel,
    GridEventListener,
    GridRowId,
    GridToolbarContainer,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { managePricingContext } from 'pages/managePricing/managePricingResolver';
import { PropsWithChildren, useContext } from 'react';
import PinColumnsButton from './PinColumnsButton';

export interface SelectedCellParams {
    id: GridRowId;
    field: string;
    isEditableField: boolean;
}

export interface IPinnedColumnsProps {
    pinnable?: boolean;
    pinAllColumns?: boolean;
    pinnedColumnsStorage?: string;
    pinnedColumnsDefault?: string[];
    handlePinColumnsModalOpen?: () => boolean;
}

export interface ManualSaveConfigProps {
    hideManualSave: boolean;
    handleIsEditableRow: (rowId: string) => boolean;
    editButtonText?: string;
}

export interface ManualSaveToolbarConfigProps {
    hideManualSave: boolean;
    getSaveHandler: (id: string) => boolean;
    isRowEditLoading: boolean;
    selectedCellParams: SelectedCellParams | null;
    cellModesModel: GridCellModesModel;
    setCellModesModel: (value: GridCellModesModel) => void;
    cellMode: 'view' | 'edit';
    handleCellFocus: (event: React.FocusEvent<HTMLDivElement>) => void;
    handleCellKeyDown: GridEventListener<'cellKeyDown'>;
    editButtonText?: string;
}

type CustomToolBarProps = PropsWithChildren<{
    manualSaveConfigProps?: ManualSaveToolbarConfigProps;
    isExportLoading: boolean;
    hideExport?: boolean;
    title?: string;
    pinnedColumnsProps?: IPinnedColumnsProps;
    setFilterButtonEl?: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
    getExportHandler: () => boolean;
    isChildrenInlineWithGridToolbarComponents?: boolean;
}>;

export function CustomToolbar({
    setFilterButtonEl,
    getExportHandler,
    isExportLoading,
    hideExport,
    manualSaveConfigProps,
    title,
    pinnedColumnsProps,
    children,
    isChildrenInlineWithGridToolbarComponents,
}: CustomToolBarProps) {
    const { response } = useContext(managePricingContext);
    const isRowExisting = !!response?.data?.rows.find((r) => r.id === manualSaveConfigProps?.selectedCellParams?.id);
    const isLoading = !!response?.isFetching || !!response?.isLoading;
    const handleMouseDown = (event: React.MouseEvent) => {
        // Keep the focus in the cell
        event.preventDefault();
    };

    const handleSaveOrEdit = () => {
        if (!manualSaveConfigProps) {
            return;
        }

        if (
            !manualSaveConfigProps.selectedCellParams ||
            !manualSaveConfigProps.cellModesModel ||
            !manualSaveConfigProps.setCellModesModel
        ) {
            return;
        }
        const { id, field } = manualSaveConfigProps?.selectedCellParams;
        if (manualSaveConfigProps.cellMode === 'edit') {
            manualSaveConfigProps.setCellModesModel({
                ...manualSaveConfigProps.cellModesModel,
                [id]: { ...manualSaveConfigProps.cellModesModel[id], [field]: { mode: GridCellModes.View } },
            });
        } else {
            manualSaveConfigProps.setCellModesModel({
                ...manualSaveConfigProps.cellModesModel,
                [id]: { ...manualSaveConfigProps.cellModesModel[id], [field]: { mode: GridCellModes.Edit } },
            });
        }
    };

    const handleCancel = () => {
        if (!manualSaveConfigProps) {
            return;
        }

        if (
            !manualSaveConfigProps.selectedCellParams ||
            !manualSaveConfigProps.cellModesModel ||
            !manualSaveConfigProps.setCellModesModel
        ) {
            return;
        }
        const { id, field } = manualSaveConfigProps.selectedCellParams;
        manualSaveConfigProps.setCellModesModel({
            ...manualSaveConfigProps.cellModesModel,
            [id]: {
                ...manualSaveConfigProps.cellModesModel[id],
                [field]: { mode: GridCellModes.View, ignoreModifications: true },
            },
        });
    };

    return (
        <>
            <GridToolbarContainer sx={{ gap: 0.5 }}>
                {title ? <Typography variant='h6'> {title} </Typography> : null}
                {setFilterButtonEl ? <GridToolbarFilterButton ref={setFilterButtonEl} /> : null}
                {!hideExport && (
                    <Button
                        color='primary'
                        size='small'
                        startIcon={
                            isExportLoading ? (
                                <CircularProgress size={20} className='MuiSvgIcon-root MuiSvgIcon-fontSizeMedium' />
                            ) : (
                                <DownloadIcon className='MuiSvgIcon-root MuiSvgIcon-fontSizeMedium' />
                            )
                        }
                        onClick={getExportHandler}
                        disabled={isExportLoading}
                    >
                        Export
                    </Button>
                )}
                {pinnedColumnsProps?.pinnable && (
                    <PinColumnsButton onClick={pinnedColumnsProps?.handlePinColumnsModalOpen} />
                )}
                {!!manualSaveConfigProps && manualSaveConfigProps?.hideManualSave === false && (
                    <>
                        <Button
                            onClick={handleSaveOrEdit}
                            onMouseDown={handleMouseDown}
                            disabled={
                                !isRowExisting ||
                                isLoading ||
                                !manualSaveConfigProps.selectedCellParams ||
                                (manualSaveConfigProps.selectedCellParams &&
                                    !manualSaveConfigProps.selectedCellParams.isEditableField) ||
                                (manualSaveConfigProps.cellMode === 'edit' && manualSaveConfigProps.isRowEditLoading)
                            }
                            variant='outlined'
                        >
                            {manualSaveConfigProps.cellMode === 'edit' ? (
                                'Save'
                            ) : manualSaveConfigProps.isRowEditLoading ? (
                                <CircularProgress size={26} />
                            ) : (
                                manualSaveConfigProps.editButtonText ?? 'Edit'
                            )}
                        </Button>
                        {!(manualSaveConfigProps.cellMode === 'view') && (
                            <Button
                                onClick={handleCancel}
                                onMouseDown={handleMouseDown}
                                variant='outlined'
                                sx={{ ml: 1 }}
                            >
                                Cancel
                            </Button>
                        )}
                    </>
                )}
                {isChildrenInlineWithGridToolbarComponents && children ? children : null}
            </GridToolbarContainer>
            {!isChildrenInlineWithGridToolbarComponents && children && (
                <GridToolbarContainer sx={{ gap: 0.5 }}>{children}</GridToolbarContainer>
            )}
        </>
    );
}
